import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../utils/security";
import { getProjectsBaseUrl } from "../../../config/config";
import { CheckOutIdentity } from "../../../platform/types";

// Save / Check in for a checked-out document (374). The document's identity comes
// from its <jyloCheckOut> custom XML part — the add-in never asks the user which
// document this is, so this API needs no pickers and no project list. Save PUTs the
// live bytes into the draft's working file; Check in promotes the draft to a version.

export interface DocumentCheckOutState {
  documentObjectIdentifier: string;
  documentName?: string | null;
  checkedOutByResourceOwnerId: string;
  checkedOutByName?: string | null;
  chatObjectIdentifier?: string | null;
  checkedOutAt: string;
  lastSavedAt?: string | null;
  draftVersionNumber: number;
}

/** The checkout-states row, which unlike the checkout response carries the draft's id. */
interface DocumentCheckOutStateWithDraft extends DocumentCheckOutState {
  draftObjectIdentifier?: string | null;
}

export interface DocumentVersion {
  objectIdentifier: string;
  versionNumber: number;
  isCurrent: boolean;
}

export const checkoutApi = createApi({
  reducerPath: "checkoutApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "",
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["CheckOutStates"],
  endpoints: (builder) => ({
    // Used to verify a document's identity part against reality on open: is the
    // draft still open, and still held by this user? A stale part means the document
    // was checked in elsewhere, discarded, or force released.
    getCheckOutStates: builder.query<DocumentCheckOutState[], { projectObjectIdentifier: string }>({
      query: ({ projectObjectIdentifier }) =>
        `${getProjectsBaseUrl()}/projects/${projectObjectIdentifier}/documents/checkout-states`,
      providesTags: (_result, _error, { projectObjectIdentifier }) => [
        { type: "CheckOutStates", id: projectObjectIdentifier },
      ],
    }),

    // The extension comes from the host adapter rather than being hardcoded to docx:
    // the API is format-agnostic (it checks the declared extension against the
    // document's own) and stamps .xlsx and .pptx from the same code path.
    //
    // The body goes up as application/octet-stream, NOT the Office MIME type. Front
    // Door's WAF (Default Rule Set 2.1) blocks any request whose Content-Type is not on
    // its allow list, and the openxmlformats types are not on it -- the request dies at
    // the edge as a bare HTML 403 with no CORS headers and never reaches the API. Nothing
    // is lost: the API never trusted the header for the format (that is what `extension`
    // is for), and every download serves the document row's own content type, copied at
    // check out, not what this request declared.
    //
    // The type has to be on the Blob, not in `headers`: fetchBaseQuery deletes any
    // Content-Type header when the body is neither JSON nor a string (so FormData can
    // set its own boundary), and fetch then sends the Blob's own type -- which the
    // host's file reader set to the Office MIME type. A header here is silently dropped;
    // that is how the first attempt at this fix shipped and changed nothing. `slice`
    // re-types the Blob as a view over the same bytes, without copying the document.
    saveDraftContent: builder.mutation<
      void,
      {
        projectObjectIdentifier: string;
        documentObjectIdentifier: string;
        content: Blob;
        extension: string;
      }
    >({
      query: ({ projectObjectIdentifier, documentObjectIdentifier, content, extension }) => ({
        url: `${getProjectsBaseUrl()}/projects/${projectObjectIdentifier}/documents/${documentObjectIdentifier}/content?extension=${extension}`,
        method: "PUT",
        body: content.slice(0, content.size, "application/octet-stream"),
      }),
      invalidatesTags: (_result, _error, { projectObjectIdentifier }) => [
        { type: "CheckOutStates", id: projectObjectIdentifier },
      ],
    }),

    // Save to Jylo for a document that did not come from Jylo: once it is uploaded, check
    // it out so the open document can be stamped as that draft. The answer is the stamp.
    //
    // Two requests because the checkout response leaves out the draft's own id (its
    // ToStateModel never sets it), and the stamp is useless without it. The states
    // listing carries it.
    checkOutDocument: builder.mutation<
      CheckOutIdentity,
      { projectObjectIdentifier: string; documentObjectIdentifier: string; deviceLabel: string }
    >({
      async queryFn(
        { projectObjectIdentifier, documentObjectIdentifier, deviceLabel },
        _api,
        _extra,
        fetchWithBQ
      ) {
        const base = `${getProjectsBaseUrl()}/projects/${projectObjectIdentifier}/documents`;

        const checkedOut = await fetchWithBQ({
          url: `${base}/${documentObjectIdentifier}/checkout`,
          method: "POST",
          body: { deviceLabel },
        });
        if (checkedOut.error) return { error: checkedOut.error };

        const states = await fetchWithBQ(`${base}/checkout-states`);
        if (states.error) return { error: states.error };

        const state = (states.data as DocumentCheckOutStateWithDraft[]).find(
          (s) => s.documentObjectIdentifier === documentObjectIdentifier
        );
        if (!state?.draftObjectIdentifier) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Jylo checked the document out but did not say which draft it opened.",
            },
          };
        }

        return {
          data: {
            projectObjectIdentifier,
            documentObjectIdentifier,
            draftObjectIdentifier: state.draftObjectIdentifier,
            // What the API stamps: the version the draft was taken from.
            baseVersionNumber: state.draftVersionNumber - 1,
          },
        };
      },
      invalidatesTags: (_result, _error, { projectObjectIdentifier }) => [
        { type: "CheckOutStates", id: projectObjectIdentifier },
      ],
    }),

    checkInDocument: builder.mutation<
      DocumentVersion,
      { projectObjectIdentifier: string; documentObjectIdentifier: string; versionComment?: string }
    >({
      query: ({ projectObjectIdentifier, documentObjectIdentifier, versionComment }) => ({
        url: `${getProjectsBaseUrl()}/projects/${projectObjectIdentifier}/documents/${documentObjectIdentifier}/checkin`,
        method: "POST",
        body: { versionComment: versionComment || undefined, force: false },
      }),
      invalidatesTags: (_result, _error, { projectObjectIdentifier }) => [
        { type: "CheckOutStates", id: projectObjectIdentifier },
      ],
    }),
  }),
});

export const {
  useGetCheckOutStatesQuery,
  useSaveDraftContentMutation,
  useCheckInDocumentMutation,
  useCheckOutDocumentMutation,
} = checkoutApi;
